/**
 * Navigation Configuration - Single Source of Truth
 *
 * Rollen-Modell (verbindlich):
 * - WORKER: Nur Produktion (WSA, Vorarbeiter)
 * - ADMIN: Alles außer Audit-Log
 * - SUPERADMIN: Wie ADMIN + Audit-Log
 * - SYSTEM: Alles + System-Bereich
 *
 * Menü-Struktur v1:
 * 1. Produktion (WORKER+) - WSA-Erfassung, Vorarbeiter-Erfassung
 * 2. Auswertung (ADMIN+) - Reports
 * 3. Stammdaten (ADMIN+) - Kunden (→Kundengruppen), Benutzer
 * 4. Maschinen/Anlagen (ADMIN+) - Maschinen (→Typen), Programme (→Typen)
 * 5. Produktionseinstellungen (ADMIN+) - Strecken, Sammelposten, Schichten, Wäscheklassen, Störungskategorien
 * 6. Audit (SUPERADMIN+) - Audit-Log
 * 7. System (SUPERADMIN+) - Stationen, Ingest Devices, Feature-Flags (nur SYSTEM)
 *
 * Submenus via parentId:
 * - Kunden → Kundengruppen
 * - Maschinen → Maschinentypen
 * - Programme → Programmtypen
 */

// =============================================================================
// TYPES
// =============================================================================

export type NavCategory =
  | 'dashboard'
  | 'produktion'
  | 'auswertung'
  | 'stammdaten'
  | 'maschinen'
  | 'produktionseinstellungen'
  | 'audit'
  | 'system'
  | 'technik'
  | 'demos';

export type UserRole =
  | 'WORKER'
  | 'FOREMAN'
  | 'ADMIN'
  | 'SUPERADMIN'
  | 'SYSTEM'
  | 'TECHNICIAN';

export type IconKey =
  | 'Home'
  | 'LayoutDashboard'
  | 'WashingMachine'
  | 'Route'
  | 'BarChart3'
  | 'AlertTriangle'
  | 'Building2'
  | 'ListChecks'
  | 'Clock'
  | 'Monitor'
  | 'Users'
  | 'Cog'
  | 'Tag'
  | 'FileText'
  | 'Settings'
  | 'UserCircle'
  | 'Wrench'
  | 'Cpu'
  | 'ChevronDown'
  | 'ChevronRight'
  | 'Layers'
  | 'MessageSquare';

export interface NavItem {
  id: string;
  label: string;
  href: string;
  category: NavCategory;
  iconKey: IconKey;
  rolesAllowed: UserRole[];
  moduleKey?: string;
  skipModuleFilter?: boolean; // Badge zeigen, aber nicht nach Modul filtern
  superadminOnly?: boolean;
  systemOnly?: boolean;
  sortOrder: number;
  parentId?: string; // NEU: Für Submenus - verweis auf Parent-Item ID
}

export interface NavGroup {
  id: NavCategory;
  label: string;
  sortOrder: number;
  defaultCollapsed?: boolean;
  superadminOnly?: boolean;
  systemOnly?: boolean;
}

export interface CrossNavItem {
  id: string;
  label: string;
  href: string;
  iconKey: IconKey;
  rolesAllowed: UserRole[];
  moduleKey?: string;
}

// =============================================================================
// NAVIGATION GROUPS
// =============================================================================

export const NAV_GROUPS: NavGroup[] = [
  { id: 'produktion', label: 'Produktion', sortOrder: 1 },
  { id: 'auswertung', label: 'Auswertung', sortOrder: 2 },
  { id: 'stammdaten', label: 'Stammdaten', sortOrder: 3 },
  { id: 'maschinen', label: 'Maschinen / Anlagen', sortOrder: 4 },
  { id: 'produktionseinstellungen', label: 'Produktionseinstellungen', sortOrder: 5 },
  { id: 'audit', label: 'Audit', sortOrder: 6, superadminOnly: true },
  { id: 'system', label: 'System', sortOrder: 7, superadminOnly: true },
  // Entwuerfe und Bedien-Prototypen - bewusst ganz unten und eingeklappt,
  // damit sie nicht mit fertigen Funktionen verwechselt werden.
  { id: 'demos', label: 'Demos (Entwürfe)', sortOrder: 8, defaultCollapsed: true },
];

// =============================================================================
// NAVIGATION ITEMS (Admin-Bereich)
// =============================================================================

export const NAV_ITEMS: NavItem[] = [
  // === DASHBOARD (Top-Level, keine Gruppe) ===
  {
    id: 'dashboard',
    label: 'Dashboard',
    href: '/admin',
    category: 'dashboard',
    iconKey: 'LayoutDashboard',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 1,
  },
  {
    id: 'changelog',
    label: 'Neuigkeiten',
    href: '/admin/changelog',
    category: 'dashboard',
    iconKey: 'FileText',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 2,
  },

  // === PRODUKTION (WORKER + FOREMAN + ADMIN + SUPERADMIN + SYSTEM) ===
  {
    id: 'wsa-erfassung',
    label: 'WSA-Erfassung',
    href: '/wsa',
    category: 'produktion',
    iconKey: 'WashingMachine',
    rolesAllowed: ['WORKER', 'FOREMAN', 'ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 1,
  },
  {
    id: 'vorarbeiter-erfassung',
    label: 'Vorarbeiter-Erfassung',
    href: '/vorarbeiter',
    category: 'produktion',
    iconKey: 'Route',
    rolesAllowed: ['WORKER', 'FOREMAN', 'ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 2,
  },

  // === AUSWERTUNG (ADMIN + SUPERADMIN + SYSTEM) ===
  {
    id: 'wsa-report',
    label: 'WSA-Report',
    href: '/reports/wsa',
    category: 'auswertung',
    iconKey: 'WashingMachine',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 1,
  },
  {
    id: 'twa-report',
    label: 'TWA-Report',
    href: '/reports/twa',
    category: 'auswertung',
    iconKey: 'Cog',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 2,
  },
  {
    id: 'processing-report',
    label: 'Vorarbeiter-Report',
    href: '/reports/processing',
    category: 'auswertung',
    iconKey: 'Route',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 3,
  },
  {
    id: 'wash-performance',
    label: 'Waschleistung',
    href: '/reports/wash-performance',
    category: 'auswertung',
    iconKey: 'BarChart3',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 4,
  },

  // === STAMMDATEN (ADMIN + SUPERADMIN + SYSTEM) ===
  // Kunden mit Submenu Kundengruppen
  {
    id: 'customers',
    label: 'Kunden',
    href: '/admin/customers',
    category: 'stammdaten',
    iconKey: 'Building2',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 1,
  },
  {
    id: 'customer-groups',
    label: 'Kundengruppen',
    href: '/admin/customer-groups',
    category: 'stammdaten',
    iconKey: 'Users',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 2,
    parentId: 'customers', // Submenu unter Kunden
  },
  {
    id: 'users',
    label: 'Benutzer',
    href: '/admin/users',
    category: 'stammdaten',
    iconKey: 'Users',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 3,
  },

  // === MASCHINEN / ANLAGEN (ADMIN + SUPERADMIN + SYSTEM) ===
  // Maschinen mit Submenu Maschinentypen
  {
    id: 'machines',
    label: 'Maschinen',
    href: '/admin/machines',
    category: 'maschinen',
    iconKey: 'WashingMachine',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 1,
  },
  {
    id: 'machine-types',
    label: 'Maschinentypen',
    href: '/admin/machine-types',
    category: 'maschinen',
    iconKey: 'Cog',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 2,
    parentId: 'machines', // Submenu unter Maschinen
  },
  // Programme mit Submenu Programmtypen
  {
    id: 'programs',
    label: 'Programme',
    href: '/admin/programs',
    category: 'maschinen',
    iconKey: 'ListChecks',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 3,
  },
  {
    id: 'program-types',
    label: 'Programmtypen',
    href: '/admin/program-types',
    category: 'maschinen',
    iconKey: 'Tag',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 4,
    parentId: 'programs', // Submenu unter Programme
  },

  // === PRODUKTIONSEINSTELLUNGEN (ADMIN + SUPERADMIN + SYSTEM) ===
  {
    id: 'lines',
    label: 'Strecken',
    href: '/admin/lines',
    category: 'produktionseinstellungen',
    iconKey: 'Route',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 1,
  },
  {
    id: 'system-buckets',
    label: 'Sammelposten',
    href: '/admin/system-buckets',
    category: 'produktionseinstellungen',
    iconKey: 'Layers',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 2,
  },
  {
    id: 'shifts',
    label: 'Schichten',
    href: '/admin/shifts',
    category: 'produktionseinstellungen',
    iconKey: 'Clock',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 3,
  },
  {
    id: 'laundry-classes',
    label: 'Wäscheklassen',
    href: '/admin/laundry-classes',
    category: 'produktionseinstellungen',
    iconKey: 'Layers',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 4,
  },
  {
    id: 'article-groups',
    label: 'Artikelgruppen',
    href: '/admin/article-groups',
    category: 'produktionseinstellungen',
    iconKey: 'Tag',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 5,
  },
  {
    id: 'disruption-categories',
    label: 'Störungskategorien',
    href: '/admin/disruption-categories',
    category: 'produktionseinstellungen',
    iconKey: 'AlertTriangle',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    moduleKey: 'disruptions',
    skipModuleFilter: true,
    sortOrder: 6,
  },
  // === DEMOS (Entwuerfe, keine Funktionen) ===
  {
    id: 'demo-flow-wegekarte',
    label: 'Flow-Builder: Schritte + Wege',
    // Neuester Entwurf (14.08.): Schritt-Karten fuer die Reihenfolge, live
    // mitlaufende Station, Wege-Liste als Freigabe-Grundlage. Die fruehere
    // Faeden-Grafik ("Wegekarte") wurde nach Rueckmeldung entfernt.
    href: '/demo/capture-flow-builder-wegekarte',
    category: 'demos',
    iconKey: 'Route',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 1,
  },
  {
    id: 'demo-flow-slides',
    label: 'Flow-Builder: Folien',
    // Demo 4, abgestimmter Stand vom 11.08., vgl. docs/CAPTURE-FLOW-BUILDER-KONZEPT.md
    href: '/demo/capture-flow-builder-slides',
    category: 'demos',
    iconKey: 'Route',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 2,
  },
  {
    id: 'demo-flow-tree',
    label: 'Flow-Builder: Baum',
    href: '/demo/capture-flow-builder-tree',
    category: 'demos',
    iconKey: 'Route',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 3,
  },
  {
    id: 'demo-flow-erste-fassung',
    label: 'Flow-Builder: erste Fassung',
    href: '/demo/capture-flow-builder',
    category: 'demos',
    iconKey: 'Route',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 4,
  },
  // === AUDIT (nur SUPERADMIN + SYSTEM) ===
  {
    id: 'audit-log',
    label: 'Audit-Log',
    href: '/admin/audit',
    category: 'audit',
    iconKey: 'FileText',
    rolesAllowed: ['SUPERADMIN', 'SYSTEM'],
    superadminOnly: true,
    sortOrder: 1,
  },

  // === SYSTEM (SUPERADMIN + SYSTEM) ===
  {
    id: 'stations',
    label: 'Stationen',
    href: '/admin/stations',
    category: 'system',
    iconKey: 'Monitor',
    rolesAllowed: ['SUPERADMIN', 'SYSTEM'],
    sortOrder: 1,
  },
  {
    id: 'ingest-devices',
    label: 'Ingest Devices',
    href: '/admin/ingest-devices',
    category: 'system',
    iconKey: 'Cpu',
    rolesAllowed: ['SUPERADMIN', 'SYSTEM'],
    sortOrder: 2,
  },
  {
    id: 'feedback',
    label: 'Meldungen',
    href: '/admin/feedback',
    category: 'system',
    iconKey: 'MessageSquare',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 3,
  },
  {
    id: 'module-flags',
    label: 'Feature-Flags',
    href: '/admin/system/module-flags',
    category: 'system',
    iconKey: 'Settings',
    rolesAllowed: ['SYSTEM'],
    systemOnly: true,
    sortOrder: 4,
  },
];

// =============================================================================
// TECH NAVIGATION ITEMS (separates Layout)
// =============================================================================

export const TECH_NAV_ITEMS: NavItem[] = [
  {
    id: 'tech-home',
    label: 'Start',
    href: '/tech',
    category: 'technik',
    iconKey: 'Wrench',
    rolesAllowed: ['TECHNICIAN', 'ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 1,
  },
  {
    id: 'tech-disruptions',
    label: 'Störungen',
    href: '/tech/disruptions',
    category: 'technik',
    iconKey: 'AlertTriangle',
    rolesAllowed: ['TECHNICIAN', 'ADMIN', 'SUPERADMIN', 'SYSTEM'],
    moduleKey: 'disruptions',
    sortOrder: 2,
  },
];

// =============================================================================
// MOBILE NAVIGATION ITEMS (Bottom-Nav, max 4 + Mehr)
// =============================================================================

export const MOBILE_NAV_ITEMS: NavItem[] = [
  {
    id: 'mobile-dashboard',
    label: 'Dashboard',
    href: '/admin',
    category: 'dashboard',
    iconKey: 'LayoutDashboard',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 1,
  },
  {
    id: 'mobile-wsa',
    label: 'WSA',
    href: '/reports/wsa',
    category: 'auswertung',
    iconKey: 'WashingMachine',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 2,
  },
  {
    id: 'mobile-vorarbeiter',
    label: 'Vorarbeiter',
    href: '/reports/processing',
    category: 'auswertung',
    iconKey: 'Route',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    sortOrder: 3,
  },
  {
    id: 'mobile-disruptions',
    label: 'Störungen',
    href: '/admin/disruptions',
    category: 'auswertung',
    iconKey: 'AlertTriangle',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
    moduleKey: 'disruptions',
    sortOrder: 4,
  },
];

// =============================================================================
// CROSS-NAVIGATION ITEMS
// =============================================================================

export const CROSS_NAV_ITEMS: Record<string, CrossNavItem> = {
  techToAdmin: {
    id: 'cross-tech-to-admin',
    label: 'Administration',
    href: '/admin',
    iconKey: 'Settings',
    rolesAllowed: ['ADMIN', 'SUPERADMIN', 'SYSTEM'],
  },
};

// =============================================================================
// ICON MAP (für dynamisches Rendering)
// =============================================================================

import {
  Home,
  LayoutDashboard,
  WashingMachine,
  Route,
  BarChart3,
  AlertTriangle,
  Building2,
  ListChecks,
  Clock,
  Monitor,
  Users,
  Cog,
  Tag,
  FileText,
  Settings,
  UserCircle,
  Wrench,
  Cpu,
  ChevronDown,
  ChevronRight,
  Layers,
  MessageSquare,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export const ICON_MAP: Record<IconKey, LucideIcon> = {
  Home,
  LayoutDashboard,
  WashingMachine,
  Route,
  BarChart3,
  AlertTriangle,
  Building2,
  ListChecks,
  Clock,
  Monitor,
  Users,
  Cog,
  Tag,
  FileText,
  Settings,
  UserCircle,
  Wrench,
  Cpu,
  ChevronDown,
  ChevronRight,
  Layers,
  MessageSquare,
};

/**
 * Sicherer Icon-Getter mit Fallback
 * Fallback: Home Icon (neutrales Icon bei fehlendem/ungültigem Key)
 */
export function getIcon(iconKey: IconKey | string): LucideIcon {
  return ICON_MAP[iconKey as IconKey] ?? Home;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Filtert NavItems nach Rolle und Modul-Status
 */
export function filterNavItems(
  items: NavItem[],
  userRole: UserRole,
  isModuleEnabled: (key: string) => boolean,
): NavItem[] {
  const isSystem = userRole === 'SYSTEM';
  const isSuperadmin = userRole === 'SUPERADMIN';

  return items.filter((item) => {
    if (item.systemOnly && !isSystem) return false;
    if (item.superadminOnly && !isSuperadmin && !isSystem) return false;
    if (!item.rolesAllowed.includes(userRole)) return false;
    if (item.moduleKey && !item.skipModuleFilter && !isSystem && !isModuleEnabled(item.moduleKey)) return false;
    return true;
  });
}

/**
 * Gruppiert NavItems nach Kategorie
 */
export function groupNavItems(items: NavItem[]): Map<NavCategory, NavItem[]> {
  const grouped = new Map<NavCategory, NavItem[]>();

  for (const item of items) {
    const existing = grouped.get(item.category) || [];
    existing.push(item);
    grouped.set(item.category, existing);
  }

  for (const [category, categoryItems] of grouped) {
    grouped.set(
      category,
      categoryItems.sort((a, b) => a.sortOrder - b.sortOrder),
    );
  }

  return grouped;
}

/**
 * Filtert NavGroups nach Rolle
 */
export function filterNavGroups(
  groups: NavGroup[],
  userRole: UserRole,
): NavGroup[] {
  const isSystem = userRole === 'SYSTEM';
  const isSuperadmin = userRole === 'SUPERADMIN';

  return groups
    .filter((group) => {
      if (group.systemOnly && !isSystem) return false;
      if (group.superadminOnly && !isSuperadmin && !isSystem) return false;
      return true;
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);
}
